import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Play, Loader2, ChevronRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { API, useAuth } from "@/App";
import { authHeaders } from "@/lib/firebase";

const MyMemories = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    if (user) {
      fetchMemories();
    }
  }, [user]);

  const fetchMemories = async () => {
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/users/me/memories`, { headers });
      if (response.ok) {
        const data = await response.json();
        setMemories(data.memories || []);
      }
    } catch (error) {
      console.error("Failed to fetch memories:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  if (loading) {
    return (
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-4">
          <Camera className="w-5 h-5 text-primary" />
          <h2 className="font-display text-lg font-bold">My Memories</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (memories.length === 0) {
    return null;
  }

  // Show only first 3 in preview
  const previewMemories = memories.slice(0, 3);
  const totalCount = memories.length;

  return (
    <div className="mt-6" data-testid="my-memories-section">
      {/* Header - clickable to go to full memories page */}
      <button 
        onClick={() => navigate("/memories")}
        className="flex items-center justify-between w-full mb-4 group"
      >
        <div className="flex items-center gap-2">
          <Camera className="w-5 h-5 text-primary" />
          <h2 className="font-display text-lg font-bold group-hover:text-primary transition-colors">My Memories</h2>
          <span className="text-sm text-muted-foreground">({totalCount})</span>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
      </button>
      
      {/* 3-column grid - max 3 photos */}
      <div className="grid grid-cols-3 gap-0.5">
        {previewMemories.map((media) => (
          <button
            key={media.media_id}
            onClick={() => setSelectedMedia(media)}
            className="relative aspect-square overflow-hidden bg-muted group"
          >
            {media.file_type === "video" ? (
              <>
                <video src={media.file_url} className="w-full h-full object-cover" />
                <div className="absolute top-2 right-2">
                  <Play className="w-5 h-5 text-white drop-shadow-lg" fill="white" />
                </div>
              </>
            ) : (
              <img src={media.file_url} alt="" className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
          </button>
        ))}
      </div>

      {/* View all link if more than 3 */}
      {totalCount > 3 && (
        <button 
          onClick={() => navigate("/memories")}
          className="w-full mt-3 py-2 text-sm font-medium text-foreground hover:text-foreground/70 underline underline-offset-4 transition-colors"
        >
          View all {totalCount} memories →
        </button>
      )}

      {/* Media Viewer Dialog */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden bg-card border-border">
          {selectedMedia && (
            <>
              <div className="relative bg-black aspect-square">
                {selectedMedia.file_type === "video" ? (
                  <video
                    src={selectedMedia.file_url}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <img
                    src={selectedMedia.file_url}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
              
              <div className="p-4">
                <button 
                  onClick={() => {
                    setSelectedMedia(null);
                    navigate(`/game/${selectedMedia.game_id}`);
                  }}
                  className="text-left hover:text-primary transition-colors"
                >
                  <p className="font-medium text-sm">{selectedMedia.game_title}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(selectedMedia.game_date)}</p>
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyMemories;
